import { Frame, parseFramesFromRaw } from "./events";

const NL2 = /\r?\n\r?\n/;

/**
 * Parse an SSE response body into a stream of typed Frames.
 * Wire format (backend): `data: {json}\n\n` only. No `event:` or `id:` lines.
 * Comment lines (starting `:`) and unknown line prefixes are ignored.
 */
export async function* parseSSEStream(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<Frame, void, void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.search(NL2)) !== -1) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx).replace(NL2, "");
        for (const frame of decodeEventAll(rawEvent)) {
          yield frame;
        }
      }
    }
    const tail = buffer.trim();
    if (tail) {
      for (const frame of decodeEventAll(tail)) {
        yield frame;
      }
    }
  } finally {
    // Cancel, not just releaseLock(): an abandoned turn (new send, regenerate,
    // unmount) must close its HTTP connection, or old turns pile up open
    // streams (ELASV1-3514). A no-op on a body that already ended.
    try {
      await reader.cancel();
    } catch {
      /* already errored or aborted: nothing left to close */
    }
    reader.releaseLock();
  }
}

function decodeEventAll(rawEvent: string): Frame[] {
  const lines = rawEvent.split(/\r?\n/);
  const dataParts: string[] = [];
  for (const line of lines) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("data:")) {
      dataParts.push(line.slice(5).trimStart());
    }
  }
  if (dataParts.length === 0) return [];
  const dataStr = dataParts.join("\n");
  try {
    const obj = JSON.parse(dataStr);
    return parseFramesFromRaw(obj);
  } catch {
    return parseFramesFromRaw({ type: "__unknown__", raw: dataStr });
  }
}
